"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDown01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";
import { ResponsivePopover } from "../../ui/responsive-popover";
import { Typography } from "../../ui/typography";
import { Slider } from "@/components/ui/slider";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatNumber,
  formatNumberInput,
  parseFormattedNumber,
} from "@/utils/format/formatNumber";

interface PriceRangePickerProps {
  className?: string;
  priceRange?: {
    from: string;
    to?: string;
  };
  onSelect?: (
    range:
      | {
          from: string;
          to?: string;
        }
      | undefined
  ) => void;
}

const isEmptyOrNumeric = (val: string) => {
  if (val === "") return true;
  const digits = parseFormattedNumber(val);
  return digits !== "" && !isNaN(Number(digits));
};

const toRawOrUndefined = (val: string) => {
  if (val === "") return undefined;
  return parseFormattedNumber(val);
};

const priceRangeSchema = z.object({
  from: z
    .string()
    .refine(isEmptyOrNumeric, {
      message: "باید عدد باشد یا خالی",
    })
    .transform(toRawOrUndefined),
  to: z
    .string()
    .refine(isEmptyOrNumeric, {
      message: "باید عدد باشد یا خالی",
    })
    .transform(toRawOrUndefined),
});

type PriceRangeFormValues = z.input<typeof priceRangeSchema>;
type PriceRangeFormOutput = z.output<typeof priceRangeSchema>;

export function PriceRangePicker({
  className,
  priceRange,
  onSelect,
}: PriceRangePickerProps) {
  const [open, setOpen] = React.useState(false);

  const form = useForm<PriceRangeFormValues, unknown, PriceRangeFormOutput>({
    resolver: zodResolver(priceRangeSchema),
    defaultValues: {
      from: priceRange?.from ?? "",
      to: priceRange?.to ?? "",
    },
  });

  // Sync form with prop changes
  React.useEffect(() => {
    form.reset({
      from: priceRange?.from ?? "",
      to: priceRange?.to ?? "",
    });
  }, [priceRange, form]);

  // Watch form values
  const from = form.watch("from") ?? "";
  const to = form.watch("to") ?? "";

  // Derive slider values with fallbacks for invalid inputs
  const fromDigits = parseFormattedNumber(from);
  const toDigits = parseFormattedNumber(to);
  const sliderFrom =
    fromDigits === "" || isNaN(Number(fromDigits)) ? 0 : Number(fromDigits);
  const sliderTo =
    toDigits === "" || isNaN(Number(toDigits)) ? 50000000 : Number(toDigits);

  const handleSliderValueChange = (values: number[]) => {
    form.setValue("from", values[0].toString(), { shouldValidate: true });
    form.setValue("to", values[1].toString(), { shouldValidate: true });
  };

  const onSubmit = (data: PriceRangeFormOutput) => {
    if (data.from || data.to) {
      onSelect?.({
        from: data.from ?? "",
        to: data.to,
      });
    } else {
      onSelect?.(undefined);
    }
    setOpen(false);
  };

  const handleDelete = () => {
    form.reset({ from: "", to: "" });
    onSelect?.(undefined);
  };

  const handleConfirm = form.handleSubmit(onSubmit);

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      className="md:w-[424px]"
      direction="bottom"
      sideOffset={16}
      trigger={
        <Button
          id="price"
          variant="pill"
          className={cn(
            "transition-colors duration-500",
            priceRange ? "border-primary text-primary" : "",
            className
          )}
        >
          {priceRange?.from ? (
            priceRange.to ? (
              <>
                {formatNumberInput(priceRange.from)} -{" "}
                {formatNumberInput(priceRange.to)}
              </>
            ) : (
              formatNumberInput(priceRange.from)
            )
          ) : (
            <Typography variant="captionRegular">قیمت برای هر شب</Typography>
          )}
          <HugeiconsIcon icon={ArrowDown01Icon} />
        </Button>
      }
    >
      <Typography
        variant="bodySMedium"
        className="py-4 border-b text-center text-foreground mb-4 md:hidden block"
      >
        تعیین قیمت
      </Typography>
      <div className="space-y-3 p-2 md:p-4">
        <div className="flex items-center gap-4 md:gap-5 mb-6">
          <div className="relative flex-1">
            <Label
              className="text-[10px] font-medium text-gray-500 absolute z-10 -top-2.5 right-2 bg-background p-1"
              htmlFor="from"
            >
              از
            </Label>
            <Controller
              name="from"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    id="from"
                    type="text"
                    inputMode="numeric"
                    placeholder="۰ تومان"
                    className="rounded-xl border-gray-200 h-10 placeholder:text-black-200 font-normal !text-xs"
                    value={formatNumberInput(field.value ?? "")}
                    onChange={(e) =>
                      field.onChange(parseFormattedNumber(e.target.value))
                    }
                  />
                  {fieldState.error && (
                    <Typography
                      variant="captionRegular"
                      className="text-red-500"
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </div>
          <div className="relative flex-1">
            <Label
              className="text-[10px] font-medium text-gray-500 absolute z-10 -top-2.5 right-2 bg-background p-1"
              htmlFor="to"
            >
              تا
            </Label>
            <Controller
              name="to"
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    id="to"
                    type="text"
                    inputMode="numeric"
                    placeholder="۵۰٬۰۰۰٬۰۰۰ تومان"
                    className="rounded-xl border-gray-200 h-10 placeholder:text-black-200 font-normal !text-xs"
                    value={formatNumberInput(field.value ?? "")}
                    onChange={(e) =>
                      field.onChange(parseFormattedNumber(e.target.value))
                    }
                  />
                  {fieldState.error && (
                    <Typography
                      variant="captionRegular"
                      className="text-red-500"
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </div>
        </div>

        <Slider
          value={[sliderFrom, sliderTo]}
          onValueChange={handleSliderValueChange}
          min={0}
          max={50000000}
          step={1000}
        />

        <div className="flex justify-between text-sm text-muted-foreground">
          <Typography variant="captionRegular">
            {formatNumber(sliderFrom)} تومان
          </Typography>
          <Typography variant="captionRegular">
            {formatNumber(sliderTo)} تومان
          </Typography>
        </div>
      </div>
      <div className="flex justify-between gap-10 items-center mt-6 md:mt-0 md:border-t px-2 md:p-4">
        <Button variant="ghost" onClick={handleDelete} className="p-1 md:p-1">
          <HugeiconsIcon
            className="size-[18px] hidden md:block"
            icon={Cancel01Icon}
          />
          <Typography variant="bodySRegular">حذف</Typography>
        </Button>
        <Button
          onClick={handleConfirm}
          className="flex-1 md:flex-none h-[50px] md:h-[35px] rounded-lg font-normal"
        >
          <Typography variant="captionMedium" className="md:hidden">
            اعمال تغییرات
          </Typography>
          <Typography
            variant="captionRegular"
            className="hidden md:inline px-1"
          >
            اعمال
          </Typography>
        </Button>
      </div>
    </ResponsivePopover>
  );
}
